/**
 * Maps raw keyboard/mouse events to abstract game actions, so gamepads or
 * touch could be added later without touching gameplay code.
 */

export type Action = 'forward' | 'back' | 'left' | 'right' | 'sprint' | 'jump' | 'paint';

const KEY_BINDINGS: Record<string, Action> = {
  KeyW: 'forward',
  ArrowUp: 'forward',
  KeyS: 'back',
  ArrowDown: 'back',
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyD: 'right',
  ArrowRight: 'right',
  ShiftLeft: 'sprint',
  ShiftRight: 'sprint',
  Space: 'jump',
};

export type Hotkey = 'camera' | 'photo' | 'help' | 'mute' | 'menu' | 'hud';

export interface InputEvents {
  /** Accumulated mouse look deltas since last frame, in pixels. */
  onLook?: (dx: number, dy: number) => void;
  /** Mouse wheel steps (+1 / -1) for spray size. */
  onWheel?: (steps: number) => void;
  /** Number keys 1..9 → palette slot 0..8. */
  onPaletteKey?: (slot: number) => void;
  /** Named hotkeys. */
  onHotkey?: (key: Hotkey) => void;
}

const HOTKEYS: Record<string, Hotkey> = {
  KeyV: 'camera',
  KeyP: 'photo',
  KeyH: 'help',
  KeyM: 'mute',
  Escape: 'menu',
  Tab: 'hud',
};

export class InputManager {
  private actions = new Set<Action>();
  private justPressed = new Set<Action>();
  events: InputEvents = {};
  /** While false (menus open, pointer unlocked) gameplay input is ignored. */
  enabled = false;

  constructor(private readonly canvas: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', () => this.clearAll());
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('wheel', this.onWheel, { passive: false });
  }

  isDown(action: Action): boolean {
    return this.enabled && this.actions.has(action);
  }

  /** True only on the first frame the action is queried after being pressed. */
  consumePressed(action: Action): boolean {
    if (!this.enabled) return false;
    if (this.justPressed.has(action)) {
      this.justPressed.delete(action);
      return true;
    }
    return false;
  }

  clearAll(): void {
    this.actions.clear();
    this.justPressed.clear();
  }

  /** Drive an action from a non-keyboard source (on-screen touch buttons). */
  setVirtualAction(action: Action, down: boolean): void {
    if (down) {
      if (!this.actions.has(action)) this.justPressed.add(action);
      this.actions.add(action);
    } else {
      this.actions.delete(action);
    }
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Hotkeys work even while gameplay input is disabled (e.g. Escape in menus),
    // except when typing into a form field.
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

    const hotkey = HOTKEYS[e.code];
    if (hotkey) {
      e.preventDefault();
      this.events.onHotkey?.(hotkey);
      return;
    }
    if (!this.enabled) return;

    if (e.code.startsWith('Digit')) {
      const n = Number(e.code.slice(5));
      if (n >= 1 && n <= 9) {
        this.events.onPaletteKey?.(n - 1);
        return;
      }
    }
    const action = KEY_BINDINGS[e.code];
    if (action) {
      e.preventDefault();
      if (!this.actions.has(action)) this.justPressed.add(action);
      this.actions.add(action);
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    const action = KEY_BINDINGS[e.code];
    if (action) this.actions.delete(action);
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (!this.enabled || e.button !== 0) return;
    if (!this.actions.has('paint')) this.justPressed.add('paint');
    this.actions.add('paint');
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.actions.delete('paint');
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.enabled || document.pointerLockElement !== this.canvas) return;
    this.events.onLook?.(e.movementX, e.movementY);
  };

  private onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    this.events.onWheel?.(Math.sign(e.deltaY));
  };
}
